import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { webFormsService, WebForm } from '@/services/web-forms.service';
import { Plus, FormInput, ToggleLeft, ToggleRight, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

function relativeTime(ts?: string) {
  if (!ts) return 'never';
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return ts;
  }
}

interface FormCardProps {
  form: WebForm;
  onToggle: (form: WebForm) => void;
}

function FormCard({ form, onToggle }: FormCardProps) {
  const navigate = useNavigate();
  const [toggling, setToggling] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setToggling(true);
    try {
      const updated = await webFormsService.update(form.formUid, {
        isActive: !form.isActive,
      });
      onToggle(updated);
    } catch {
      // interceptor handles
    } finally {
      setToggling(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-surface p-4 cursor-pointer hover:shadow-elevated hover:-translate-y-0.5 transition-all"
      onClick={() => navigate(`/leads/web-forms/${form.formUid}`)}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
            <FormInput size={16} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm text-text-primary truncate">{form.name}</p>
            {form.description && (
              <p className="text-[11px] text-text-muted truncate">{form.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleToggle}
            disabled={toggling}
            className="text-text-muted hover:text-primary transition-colors"
            title={form.isActive ? 'Deactivate' : 'Activate'}
          >
            {form.isActive ? (
              <ToggleRight size={20} className="text-primary" />
            ) : (
              <ToggleLeft size={20} />
            )}
          </button>
          <ArrowRight size={15} className="text-text-muted" />
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-text-muted">
        <span
          className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
            form.isActive
              ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-surface-alt text-text-muted',
          )}
        >
          {form.isActive ? 'Active' : 'Inactive'}
        </span>
        <span>{form.fields.length} field{form.fields.length !== 1 ? 's' : ''}</span>
        <span>{form.submitCount} submission{form.submitCount !== 1 ? 's' : ''}</span>
        <span>Last submit {relativeTime(form.lastSubmittedAt)}</span>
      </div>
    </div>
  );
}

export default function WebFormsList() {
  const navigate = useNavigate();
  const [forms, setForms] = useState<WebForm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    webFormsService
      .list()
      .then(setForms)
      .catch(() => toast.error('Failed to load web forms'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Topbar
        title="Web Forms"
        breadcrumb="Leads / Web Forms"
        right={
          <Button iconLeft={<Plus size={14} />} onClick={() => navigate('/leads/web-forms/new')}>
            New form
          </Button>
        }
      />

      <div className="px-7 py-7 w-full">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="skeleton h-28 rounded-2xl" />
            ))}
          </div>
        ) : forms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-soft text-primary mb-4">
              <FormInput size={28} />
            </div>
            <h3 className="font-display text-lg font-semibold text-text-primary">
              No web forms yet
            </h3>
            <p className="text-sm text-text-muted mt-1 max-w-xs">
              Create an embeddable form widget and drop it on any website page. Every submission
              becomes a lead in your inbox.
            </p>
            <Button
              className="mt-5"
              iconLeft={<Plus size={14} />}
              onClick={() => navigate('/leads/web-forms/new')}
            >
              Create first form
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map((f) => (
              <FormCard
                key={f.formUid}
                form={f}
                onToggle={(updated) =>
                  setForms((prev) =>
                    prev.map((x) => (x.formUid === updated.formUid ? updated : x)),
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
